import { Link } from "wouter";
import { toast } from "sonner";
import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/ResponsiveDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ds/Icon";
import { cn } from "@/lib/utils";
import { CATEGORIA_NOVEDAD_META, type Novedad } from "@/types/novedad";

function formatFechaNovedad(fecha: string): string {
  return new Date(fecha).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

interface NovedadesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  novedades: Novedad[];
  loading: boolean;
  error: Error | null;
  sinLeer: number;
  hasUnseen: boolean;
  marcarVisto: () => Promise<void>;
}

/**
 * Resumen de novedades que abre la campana del header.
 *
 * ABRIRLO NO MARCA NADA COMO LEÍDO — igual que la pantalla completa. Antes lo
 * hacía al montar, y eso significaba que un vistazo de dos segundos borraba el
 * rastro de lo que el usuario todavía no había revisado: al volver ya no sabía
 * qué le faltaba. Marcar es una decisión, y por eso es un botón.
 */
export function NovedadesModal({
  open,
  onOpenChange,
  novedades,
  loading,
  error,
  sinLeer,
  hasUnseen,
  marcarVisto,
}: NovedadesModalProps) {
  const idsSinLeer = new Set(novedades.slice(0, sinLeer).map((n) => n.id));

  const handleMarcar = async () => {
    await marcarVisto();
    toast.success("Listo: marcamos todas las novedades como leídas.");
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Novedades</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {hasUnseen
              ? `${sinLeer} ${sinLeer === 1 ? "novedad nueva" : "novedades nuevas"} desde tu última visita.`
              : "Estás al día con lo último de Facil Control."}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <ResponsiveDialogBody>
          {loading ? (
            <p className="text-sm text-muted-foreground">Cargando novedades…</p>
          ) : error ? (
            <p className="text-sm text-muted-foreground">
              No pudimos cargar las novedades. Intenta nuevamente más tarde.
            </p>
          ) : novedades.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay novedades por ahora.</p>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {novedades.slice(0, 6).map((novedad) => {
                const meta = CATEGORIA_NOVEDAD_META[novedad.categoria];
                const nueva = idsSinLeer.has(novedad.id);
                return (
                  <li
                    key={novedad.id}
                    className={cn(
                      "flex gap-2.5 rounded-xl border p-3",
                      nueva ? "border-primary-200 bg-primary-50" : "border-border bg-card",
                    )}
                  >
                    <span
                      className={cn(
                        "flex size-[26px] flex-none items-center justify-center rounded-full",
                        meta.circuloClassName,
                      )}
                    >
                      <Icon name={meta.icono} size={15} />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="flex flex-wrap items-start gap-2">
                        <h3 className="min-w-[160px] flex-1 text-[13px] leading-snug font-semibold text-pretty">
                          {novedad.titulo}
                        </h3>
                        <Badge variant={meta.tono} className="flex-none">
                          {meta.label}
                        </Badge>
                      </div>
                      <span className="fc-num text-[11px] text-soft">
                        {formatFechaNovedad(novedad.fecha)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </ResponsiveDialogBody>

        <ResponsiveDialogFooter>
          {hasUnseen && (
            <Button variant="outline" onClick={handleMarcar}>
              <Icon name="done_all" size={16} className="text-muted-foreground" />
              Marcar como leídas
            </Button>
          )}
          <Button asChild variant="secondary">
            <Link href="/novedades" onClick={() => onOpenChange(false)}>
              Ver todas las novedades
            </Link>
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
